import { buildBuiltinCards, DEFAULT_PROMPTS } from "./content.js";

const DATABASE_NAME = "speaking-studio-iphone";
const DATABASE_VERSION = 1;
const DEFAULT_PREFERENCES = Object.freeze({
  textProvider: "openai",
  audioProvider: "ark",
  transcriptionProvider: "ark",
  speechProvider: "elevenlabs",
  dynamicFollowupSeconds: 3,
  saveRecordings: true,
  retentionDays: 90,
});

let databasePromise = null;

/**
 * 把 IndexedDB 请求转换为 Promise。
 *
 * @param {IDBRequest} request IndexedDB 原生请求。
 * @returns {Promise<any>} 请求成功结果。
 */
function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener("error", () => reject(request.error), { once: true });
  });
}

/**
 * 等待事务完整提交，避免页面立即切换导致部分数据未落盘。
 *
 * @param {IDBTransaction} transaction 需要等待的事务。
 * @returns {Promise<void>} 事务完成结果。
 */
function transactionComplete(transaction) {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener("error", () => reject(transaction.error), { once: true });
    transaction.addEventListener("abort", () => reject(transaction.error || new Error("本地数据事务已取消")), { once: true });
  });
}

/**
 * 打开并升级 PWA 的 IndexedDB 数据库。
 *
 * @returns {Promise<IDBDatabase>} 可复用的数据库连接。
 */
export function openDatabase() {
  if (databasePromise) return databasePromise;
  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("preferences")) database.createObjectStore("preferences", { keyPath: "key" });
      if (!database.objectStoreNames.contains("prompts")) database.createObjectStore("prompts", { keyPath: "id" });
      if (!database.objectStoreNames.contains("cards")) {
        const cards = database.createObjectStore("cards", { keyPath: "id" });
        cards.createIndex("source", "source", { unique: false });
        cards.createIndex("enabled", "enabled", { unique: false });
      }
      if (!database.objectStoreNames.contains("sessions")) {
        const sessions = database.createObjectStore("sessions", { keyPath: "id" });
        sessions.createIndex("createdAt", "createdAt", { unique: false });
        sessions.createIndex("kind", "kind", { unique: false });
      }
      if (!database.objectStoreNames.contains("audio")) {
        const audio = database.createObjectStore("audio", { keyPath: "id" });
        audio.createIndex("sessionId", "sessionId", { unique: false });
        audio.createIndex("createdAt", "createdAt", { unique: false });
      }
      if (!database.objectStoreNames.contains("auth")) database.createObjectStore("auth", { keyPath: "key" });
      if (!database.objectStoreNames.contains("state")) database.createObjectStore("state", { keyPath: "key" });
    });
    request.addEventListener("success", () => {
      request.result.addEventListener("versionchange", () => request.result.close());
      resolve(request.result);
    }, { once: true });
    request.addEventListener("error", () => reject(request.error), { once: true });
    request.addEventListener("blocked", () => reject(new Error("请关闭其他已打开的应用页面后重试")), { once: true });
  });
  return databasePromise;
}

/**
 * 初始化默认设置、提示词和内置题库，只补充不存在的数据。
 *
 * @returns {Promise<void>} 初始化完成结果。
 */
export async function initializeStorage() {
  const database = await openDatabase();
  const transaction = database.transaction(["preferences", "prompts", "cards"], "readwrite");
  const preferences = transaction.objectStore("preferences");
  const prompts = transaction.objectStore("prompts");
  const cards = transaction.objectStore("cards");
  const currentPreferences = await requestResult(preferences.get("app"));
  if (!currentPreferences) preferences.put({ key: "app", value: { ...DEFAULT_PREFERENCES } });
  for (const [id, value] of Object.entries(DEFAULT_PROMPTS)) {
    if (!await requestResult(prompts.get(id))) prompts.put({ id, value });
  }
  const cardCount = await requestResult(cards.count());
  if (cardCount === 0) buildBuiltinCards().forEach((card) => cards.put(card));
  await transactionComplete(transaction);
}

/**
 * 读取当前本地偏好并使用默认值补齐未来新增字段。
 *
 * @returns {Promise<object>} 完整偏好设置。
 */
export async function getPreferences() {
  const database = await openDatabase();
  const transaction = database.transaction("preferences", "readonly");
  const record = await requestResult(transaction.objectStore("preferences").get("app"));
  return { ...DEFAULT_PREFERENCES, ...(record?.value || {}) };
}

/**
 * 合并并保存应用偏好。
 *
 * @param {object} values 需要更新的设置字段。
 * @returns {Promise<object>} 保存后的完整设置。
 */
export async function savePreferences(values) {
  const database = await openDatabase();
  const current = await getPreferences();
  const next = { ...current, ...values };
  const transaction = database.transaction("preferences", "readwrite");
  transaction.objectStore("preferences").put({ key: "app", value: next });
  await transactionComplete(transaction);
  return next;
}

/**
 * 读取当前十组提示词。
 *
 * @returns {Promise<Record<string, string>>} 提示词键值对象。
 */
export async function getPrompts() {
  const database = await openDatabase();
  const transaction = database.transaction("prompts", "readonly");
  const records = await requestResult(transaction.objectStore("prompts").getAll());
  const stored = Object.fromEntries(records.map((record) => [record.id, record.value]));
  return { ...DEFAULT_PROMPTS, ...stored };
}

/**
 * 保存用户编辑后的完整提示词集合。
 *
 * @param {Record<string, string>} values 以固定提示词键为索引的文本。
 * @returns {Promise<void>} 保存完成结果。
 */
export async function savePrompts(values) {
  const database = await openDatabase();
  const transaction = database.transaction("prompts", "readwrite");
  const store = transaction.objectStore("prompts");
  for (const id of Object.keys(DEFAULT_PROMPTS)) {
    const value = String(values[id] || "").trim();
    if (!value) throw new Error("提示词不能为空");
    store.put({ id, value });
  }
  await transactionComplete(transaction);
}

/**
 * 删除用户提示词改动并恢复程序默认值。
 *
 * @returns {Promise<Record<string, string>>} 恢复后的提示词。
 */
export async function resetPrompts() {
  await savePrompts(DEFAULT_PROMPTS);
  return { ...DEFAULT_PROMPTS };
}

/**
 * 读取题库，可选过滤启用状态和搜索文本。
 *
 * @param {{enabledOnly?: boolean, search?: string}} options 题库过滤条件。
 * @returns {Promise<Array<object>>} 按来源和标题排序的题卡。
 */
export async function listCards({ enabledOnly = false, search = "" } = {}) {
  const database = await openDatabase();
  const transaction = database.transaction("cards", "readonly");
  const cards = await requestResult(transaction.objectStore("cards").getAll());
  const needle = search.trim().toLowerCase();
  return cards
    .filter((card) => (!enabledOnly || card.enabled) && (!needle || `${card.title} ${card.category} ${card.tags.join(" ")}`.toLowerCase().includes(needle)))
    .sort((left, right) => left.source.localeCompare(right.source) || left.title.localeCompare(right.title));
}

/**
 * 补齐导入题卡的可选字段，保证搜索和练习流程读取稳定。
 *
 * @param {object} card 原始题卡。
 * @returns {object} 可写入 IndexedDB 的完整题卡。
 */
function normalizeCardForStorage(card) {
  const category = String(card.category || "custom").trim() || "custom";
  return {
    ...card,
    id: card.id || `custom-${crypto.randomUUID()}`,
    title: String(card.title || "").trim(),
    cue_points: Array.isArray(card.cue_points) ? card.cue_points.map((value) => String(value).trim()).filter(Boolean) : [],
    rounding_question: String(card.rounding_question || "").trim(),
    part3_topic: String(card.part3_topic || "").trim(),
    part3_questions: Array.isArray(card.part3_questions) ? card.part3_questions.map((value) => String(value).trim()).filter(Boolean) : [],
    category,
    tags: Array.isArray(card.tags) ? card.tags.map((value) => String(value).trim()).filter(Boolean) : [category],
    source: card.source === "builtin" ? "builtin" : (card.source || "custom"),
    enabled: card.enabled !== false,
  };
}

/**
 * 新增或更新一张本地题卡。
 *
 * @param {object} card 已完成字段校验的题卡。
 * @returns {Promise<object>} 实际保存的题卡。
 */
export async function saveCard(card) {
  const saved = normalizeCardForStorage(card);
  const database = await openDatabase();
  const transaction = database.transaction("cards", "readwrite");
  transaction.objectStore("cards").put(saved);
  await transactionComplete(transaction);
  return saved;
}

/**
 * 在同一事务中批量保存题卡，避免导入失败后留下部分数据。
 *
 * @param {Array<object>} cards 已完成字段校验的题卡。
 * @returns {Promise<Array<object>>} 实际保存的题卡。
 */
export async function saveCards(cards) {
  const savedCards = cards.map(normalizeCardForStorage);
  const database = await openDatabase();
  const transaction = database.transaction("cards", "readwrite");
  const store = transaction.objectStore("cards");
  savedCards.forEach((card) => store.put(card));
  await transactionComplete(transaction);
  return savedCards;
}

/**
 * 删除一张自定义题卡；内置题卡只能停用。
 *
 * @param {string} cardId 需要删除的题卡 ID。
 * @returns {Promise<void>} 删除完成结果。
 */
export async function deleteCard(cardId) {
  const database = await openDatabase();
  const transaction = database.transaction("cards", "readwrite");
  const store = transaction.objectStore("cards");
  const card = await requestResult(store.get(cardId));
  if (!card) throw new Error("题卡不存在");
  if (card.source === "builtin") throw new Error("内置题卡不能永久删除，可以将其停用");
  store.delete(cardId);
  await transactionComplete(transaction);
}

/**
 * 更新题卡启用状态。
 *
 * @param {string} cardId 题卡 ID。
 * @param {boolean} enabled 新启用状态。
 * @returns {Promise<object>} 更新后的题卡。
 */
export async function setCardEnabled(cardId, enabled) {
  const database = await openDatabase();
  const transaction = database.transaction("cards", "readwrite");
  const store = transaction.objectStore("cards");
  const card = await requestResult(store.get(cardId));
  if (!card) throw new Error("题卡不存在");
  const updated = { ...card, enabled: Boolean(enabled) };
  store.put(updated);
  await transactionComplete(transaction);
  return updated;
}

/**
 * 清除全部自定义题卡并恢复最新内置题库。
 *
 * @returns {Promise<Array<object>>} 恢复后的题卡。
 */
export async function resetQuestionBank() {
  const cards = buildBuiltinCards();
  const database = await openDatabase();
  const transaction = database.transaction("cards", "readwrite");
  const store = transaction.objectStore("cards");
  store.clear();
  cards.forEach((card) => store.put(card));
  await transactionComplete(transaction);
  return cards;
}

/**
 * 保存一次完整练习及其可选录音，使用同一事务避免孤立音频。
 *
 * @param {object} session 练习结果和展示元数据。
 * @param {Array<object>} audioEntries 包含 Blob 的音频记录。
 * @returns {Promise<object>} 保存后的练习记录。
 */
export async function saveSession(session, audioEntries = []) {
  const database = await openDatabase();
  const transaction = database.transaction(["sessions", "audio"], "readwrite");
  const sessions = transaction.objectStore("sessions");
  const audio = transaction.objectStore("audio");
  const savedSession = {
    ...session,
    id: session.id || crypto.randomUUID(),
    createdAt: session.createdAt || new Date().toISOString(),
    audioIds: audioEntries.map((entry) => entry.id),
  };
  sessions.put(savedSession);
  audioEntries.forEach((entry) => audio.put({ ...entry, sessionId: savedSession.id, createdAt: entry.createdAt || savedSession.createdAt }));
  await transactionComplete(transaction);
  return savedSession;
}

/**
 * 按时间倒序读取练习历史。
 *
 * @param {number} limit 最大返回数量。
 * @returns {Promise<Array<object>>} 练习历史。
 */
export async function listSessions(limit = 100) {
  const database = await openDatabase();
  const transaction = database.transaction("sessions", "readonly");
  const sessions = await requestResult(transaction.objectStore("sessions").getAll());
  return sessions.sort((left, right) => right.createdAt.localeCompare(left.createdAt)).slice(0, Math.max(1, limit));
}

/**
 * 读取一段本地音频。
 *
 * @param {string} audioId 音频记录 ID。
 * @returns {Promise<object|undefined>} 音频记录或 undefined。
 */
export async function getAudio(audioId) {
  const database = await openDatabase();
  const transaction = database.transaction("audio", "readonly");
  return requestResult(transaction.objectStore("audio").get(audioId));
}

/**
 * 保存一段后生成的参考表达语音，并关联到所属练习。
 *
 * @param {{id:string, sessionId:string, kind:string, mimeType:string, blob:Blob}} entry 音频记录。
 * @returns {Promise<object>} 保存后的音频记录。
 */
export async function saveAudio(entry) {
  const saved = { ...entry, createdAt: entry.createdAt || new Date().toISOString() };
  const database = await openDatabase();
  const transaction = database.transaction("audio", "readwrite");
  transaction.objectStore("audio").put(saved);
  await transactionComplete(transaction);
  return saved;
}

/**
 * 删除一次练习及其全部关联录音。
 *
 * @param {string} sessionId 练习记录 ID。
 * @returns {Promise<void>} 级联删除完成结果。
 */
export async function deleteSession(sessionId) {
  const database = await openDatabase();
  const transaction = database.transaction(["sessions", "audio"], "readwrite");
  transaction.objectStore("sessions").delete(sessionId);
  const audioIndex = transaction.objectStore("audio").index("sessionId");
  const keys = await requestResult(audioIndex.getAllKeys(IDBKeyRange.only(sessionId)));
  keys.forEach((key) => transaction.objectStore("audio").delete(key));
  await transactionComplete(transaction);
}

/**
 * 清空全部练习历史并级联删除对应录音。
 *
 * @returns {Promise<void>} 清理完成结果。
 */
export async function clearHistory() {
  const database = await openDatabase();
  const transaction = database.transaction(["sessions", "audio"], "readwrite");
  transaction.objectStore("sessions").clear();
  transaction.objectStore("audio").clear();
  await transactionComplete(transaction);
}

/**
 * 删除全部录音但保留文字结果和评分。
 *
 * @returns {Promise<void>} 录音清理完成结果。
 */
export async function clearAudio() {
  const database = await openDatabase();
  const transaction = database.transaction(["sessions", "audio"], "readwrite");
  transaction.objectStore("audio").clear();
  const sessionStore = transaction.objectStore("sessions");
  const sessions = await requestResult(sessionStore.getAll());
  sessions.forEach((session) => sessionStore.put({ ...session, audioIds: [] }));
  await transactionComplete(transaction);
}

/**
 * 读取全部音频记录，仅用于用户主动导出本地备份。
 *
 * @returns {Promise<Array<object>>} 带 Blob 的音频记录。
 */
export async function listAudio() {
  const database = await openDatabase();
  const transaction = database.transaction("audio", "readonly");
  return requestResult(transaction.objectStore("audio").getAll());
}

/**
 * 保存或清除单用户访问令牌。
 *
 * @param {string} token 网关签发的访问令牌；空字符串表示退出。
 * @returns {Promise<void>} 保存完成结果。
 */
export async function saveAccessToken(token) {
  const database = await openDatabase();
  const transaction = database.transaction("auth", "readwrite");
  const store = transaction.objectStore("auth");
  if (token) store.put({ key: "accessToken", value: token });
  else store.delete("accessToken");
  await transactionComplete(transaction);
}

/**
 * 读取单用户访问令牌。
 *
 * @returns {Promise<string>} 访问令牌或空字符串。
 */
export async function getAccessToken() {
  const database = await openDatabase();
  const transaction = database.transaction("auth", "readonly");
  const record = await requestResult(transaction.objectStore("auth").get("accessToken"));
  return String(record?.value || "");
}

/**
 * 保存可跨页面重载恢复的短期练习状态。
 *
 * @param {string} key 状态键。
 * @param {any} value 可结构化克隆的数据。
 * @returns {Promise<void>} 保存完成结果。
 */
export async function saveState(key, value) {
  const database = await openDatabase();
  const transaction = database.transaction("state", "readwrite");
  transaction.objectStore("state").put({ key, value });
  await transactionComplete(transaction);
}

/**
 * 读取一项短期练习状态。
 *
 * @param {string} key 状态键。
 * @returns {Promise<any>} 已保存值或 undefined。
 */
export async function getState(key) {
  const database = await openDatabase();
  const transaction = database.transaction("state", "readonly");
  const record = await requestResult(transaction.objectStore("state").get(key));
  return record?.value;
}

/**
 * 按用户保留期限删除过期练习和关联录音。
 *
 * @param {number} retentionDays 保留天数；0 表示永久保留。
 * @returns {Promise<number>} 已删除练习数量。
 */
export async function pruneExpiredSessions(retentionDays) {
  const days = Number(retentionDays);
  if (!Number.isFinite(days) || days <= 0) return 0;
  const cutoff = Date.now() - days * 86_400_000;
  const sessions = await listSessions(Number.MAX_SAFE_INTEGER);
  const stale = sessions.filter((session) => Date.parse(session.createdAt) < cutoff);
  for (const session of stale) await deleteSession(session.id);
  return stale.length;
}

/**
 * 汇总本地数据数量和录音占用空间。
 *
 * @returns {Promise<object>} 数据管理页使用的统计值。
 */
export async function getStorageSummary() {
  const [cards, sessions, audio, prompts] = await Promise.all([listCards(), listSessions(Number.MAX_SAFE_INTEGER), listAudio(), getPrompts()]);
  const audioBytes = audio.reduce((total, item) => total + Number(item.blob?.size || 0), 0);
  return {
    cards: cards.length,
    customCards: cards.filter((card) => card.source !== "builtin").length,
    prompts: Object.keys(prompts).length,
    sessions: sessions.length,
    recordings: audio.length,
    audioBytes,
  };
}

/**
 * 生成不包含访问令牌的 JSON 备份对象。
 *
 * @returns {Promise<object>} 可序列化的本地数据快照。
 */
export async function exportSnapshot() {
  const [preferences, prompts, cards, sessions] = await Promise.all([getPreferences(), getPrompts(), listCards(), listSessions(Number.MAX_SAFE_INTEGER)]);
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    preferences,
    prompts,
    cards,
    sessions,
  };
}

/**
 * 清空所有用户数据并恢复默认题库和提示词，可选择同时退出登录。
 *
 * @param {{clearAuth?: boolean}} options 是否清除网关访问令牌。
 * @returns {Promise<void>} 恢复完成结果。
 */
export async function factoryReset({ clearAuth = false } = {}) {
  const database = await openDatabase();
  const stores = ["preferences", "prompts", "cards", "sessions", "audio", "state", ...(clearAuth ? ["auth"] : [])];
  const transaction = database.transaction(stores, "readwrite");
  stores.forEach((storeName) => transaction.objectStore(storeName).clear());
  await transactionComplete(transaction);
  await initializeStorage();
}
